import { useState, useCallback, useMemo, useRef, useEffect, useLayoutEffect } from 'react';
import { loadTableColumnWidths, persistTableColumnWidths, clearTableColumnWidths } from '../utils/tableColumnWidths';

/**
 * Column resize with persisted pixel widths (localStorage).
 * @param {Object} opts
 * @param {string} opts.storageKey
 * @param {string[]} opts.columnIds - display order (e.g. ['__checkbox__', 'depositDate', ...])
 * @param {Record<string, number>} opts.defaultWidths - px per column id
 * @param {number} [opts.minWidth=48]
 */
export function useResizableTableColumns({ storageKey, columnIds, defaultWidths, minWidth = 48 }) {
  const [overrides, setOverrides] = useState(() => loadTableColumnWidths(storageKey));
  const overridesRef = useRef(overrides);
  useEffect(() => {
    overridesRef.current = overrides;
  }, [overrides]);

  const resolvedWidths = useMemo(() => {
    const out = {};
    columnIds.forEach((id) => {
      const def = defaultWidths[id] ?? 112;
      out[id] = typeof overrides[id] === 'number' && overrides[id] >= minWidth ? overrides[id] : def;
    });
    return out;
  }, [columnIds, overrides, defaultWidths, minWidth]);

  const resolvedWidthsRef = useRef(resolvedWidths);
  const defaultWidthsRef = useRef(defaultWidths);
  useLayoutEffect(() => {
    resolvedWidthsRef.current = resolvedWidths;
  }, [resolvedWidths]);
  useLayoutEffect(() => {
    defaultWidthsRef.current = defaultWidths;
  }, [defaultWidths]);

  const Colgroup = useCallback(() => {
    const sumPx = columnIds.reduce((s, id) => s + (resolvedWidths[id] ?? 0), 0);
    const safeSum = Math.max(sumPx, 1);
    return (
      <colgroup>
        {columnIds.map((id) => {
          const px = resolvedWidths[id] ?? 0;
          const pct = (px / safeSum) * 100;
          return <col key={id} style={{ width: `${pct}%` }} />;
        })}
      </colgroup>
    );
  }, [columnIds, resolvedWidths]);

  const onResizeMouseDown = useCallback(
    (columnId) => (e) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      const handle = e.currentTarget;
      const th = handle.closest('th');
      if (!th) return;

      const pointerId = e.pointerId;
      try {
        handle.setPointerCapture(pointerId);
      } catch (_) {
        // Some environments may reject capture; listeners still work for mouse
      }

      const startGrabX = e.clientX;
      const stateW =
        resolvedWidthsRef.current[columnId] ?? defaultWidthsRef.current[columnId] ?? 112;
      // Drive width from persisted/logical px + pointer delta. Using border-box geometry here
      // (previous approach) snaps to the browser-distributed width on the first move when
      // table-layout:fixed and width:100% inflate cells beyond <col> hints.
      const startW = Math.max(minWidth, stateW);
      let lastW = startW;

      const onMove = (ev) => {
        if (ev.pointerId !== pointerId) return;
        if (ev.pointerType === 'mouse' && (ev.buttons & 1) === 0) return;
        const nextW = Math.max(minWidth, Math.round(startW + (ev.clientX - startGrabX)));
        if (nextW === lastW) return;
        lastW = nextW;
        setOverrides((prev) => {
          const next = { ...prev, [columnId]: nextW };
          overridesRef.current = next;
          return next;
        });
      };

      const onUp = (ev) => {
        if (ev.pointerId !== pointerId) return;
        try {
          handle.releasePointerCapture(pointerId);
        } catch (_) {
          /* ignore */
        }
        document.removeEventListener('pointermove', onMove);
        document.removeEventListener('pointerup', onUp);
        document.removeEventListener('pointercancel', onUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        const next = { ...overridesRef.current, [columnId]: lastW };
        overridesRef.current = next;
        setOverrides(next);
        persistTableColumnWidths(storageKey, next);
      };

      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      document.addEventListener('pointermove', onMove);
      document.addEventListener('pointerup', onUp);
      document.addEventListener('pointercancel', onUp);
    },
    [minWidth, storageKey]
  );

  const resetColumnWidths = useCallback(() => {
    const empty = {};
    overridesRef.current = empty;
    setOverrides(empty);
    clearTableColumnWidths(storageKey);
  }, [storageKey]);

  return { Colgroup, onResizeMouseDown, resolvedWidths, resetColumnWidths };
}
